import React from 'react';
import { communityUrls, companyUrls, userUrls } from 'urls';
import { DashboardMessage as DashboardMessageDTO } from 'apis/DashboardAPI';
import { Link } from 'react-router-dom';
import DashboardMessage from './DashboardMessage';
import { CompanyMessages } from 'pages/Dashboard/manage/CompanyMessages';
import styled from '@emotion/styled';
import { quarterSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';
import featureToggle from 'featureToggle';
import Logo from 'ui/domain/Logo';
import { MobileMargin } from 'ui/views/layouts/BaseLayout';
import Card from 'ui/views/cards/Card';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { getOrUndefined } from 'util/resource';
import { getLinkInfo } from 'pages/Discover/SelectedPartnerOffers/SelectedPartnerOffer/utils';
import { useCloseableMessage } from 'apis/CompanyAPI/users/useUiState';
import { IconButton } from '@mui/material';
import CloseIcon from 'ui/elements/icons/CloseIcon';
import { notEmpty } from 'util/arrayUtils';
import { stripHtml } from 'util/stringUtils';

export const healthArenaMessageKey = `has-dismissed-health-arena-message`;

const SebMessage = styled(Card)`
  background-color: #1a1a1a;
  color: ${bluePlanetTheme.bluePlanetPalette.white};
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: ${quarterSpacing};
  right: ${quarterSpacing};
  z-index: 1;
`;

export default function OnYourAgenda({ messages }: { messages: DashboardMessageDTO[] }) {
  const userProfile = getOrUndefined(useSelfUserProfile().resource);

  // Closeable message hooks for each dismissable message type
  const sebMessage = useCloseableMessage('welcome-to-seb-client-communities');
  const pendingInCommunity = useCloseableMessage('you-are-pending-in-community');
  const newInCommunity = useCloseableMessage('you-are-new-in-community');
  const pendingInvites = useCloseableMessage('pending-community-invites');

  // For partner offers, we need to call hooks at top level for each possible message
  const partnerOfferMessage = messages.find(m => m.type === 'latest-partner-offer');
  const partnerOffer = useCloseableMessage(
    partnerOfferMessage ? `partner-offer-${partnerOfferMessage.id}` : 'no-partner-offer',
  );

  // Mentor message hooks
  const mentorNoMentees = useCloseableMessage('mentor-with-no-mentees');
  const mentorOneMentee = useCloseableMessage('mentor-with-one-mentee');
  const mentorSeveralMentees = useCloseableMessage('mentor-with-several-mentees');

  const renderMessageCard = (message: DashboardMessageDTO, index: number) => {
    switch (message.type) {
      case 'welcome-to-seb-client-communities':
        if (sebMessage.isClosed) return null;
        return (
          <div key={message.type} style={{ position: 'relative' }}>
            <CloseButtonWrapper>
              <IconButton size="small" onClick={sebMessage.closeMessage} aria-label="Close" sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </CloseButtonWrapper>
            <SebMessage color="indigo" style={{ gridColumn: 'span 2' }}>
              <img
                src="https://res.cloudinary.com/crowdworks/image/upload/f_auto,q_auto/v1/marketing/seb-logo-white-p"
                height={50}
                alt="SEB logo"
              />
              <p className="u-content-spacing-top text-large">
                Welcome to the SEB client communities. Get ready to connect with industry experts, passionate
                specialists and accelerate your personal & financial growth. More communities are opening soon.
              </p>
            </SebMessage>
          </div>
        );
      case 'you-are-pending-in-community': {
        if (pendingInCommunity.isClosed) return null;
        const community = message.community;
        const isDnbCommunity = featureToggle.communityIsConnectedToDnb(community.id);

        return (
          <DashboardMessage
            key={message.type}
            color={{
              background: isDnbCommunity ? '#285358' : bluePlanetTheme.bluePlanetPalette.white,
              text: isDnbCommunity
                ? bluePlanetTheme.bluePlanetPalette.white
                : bluePlanetTheme.bluePlanetPalette.indigo.dark,
            }}
            topElement={<Logo size={70} type="community" url={community.logoURL}></Logo>}
            onClose={pendingInCommunity.closeMessage}
          >
            <p className="text-weight-medium">You are pending access to {community.name}</p>
            <p>
              Please wait for the community manager to review your profile, you will be notified as soon as you are
              approved.
            </p>
            <div style={{ marginTop: 'auto' }}>
              <Link
                className={isDnbCommunity ? 'text-link-light' : 'text-link'}
                to={communityUrls.overview(community.slug)}
              >
                View community
              </Link>
            </div>
          </DashboardMessage>
        );
      }
      case 'you-are-new-in-community': {
        if (newInCommunity.isClosed) return null;
        const community = message.community;
        const isDnbCommunity = featureToggle.communityIsConnectedToDnb(community.id);
        const joinedAs =
          message.role === 'company_admin_in_community'
            ? (message.joinedAsCompany?.name ?? 'Your company') + ' has'
            : 'You have';

        return (
          <DashboardMessage
            key={message.type}
            color={{
              background: isDnbCommunity ? '#285358' : bluePlanetTheme.bluePlanetPalette.white,
              text: isDnbCommunity
                ? bluePlanetTheme.bluePlanetPalette.white
                : bluePlanetTheme.bluePlanetPalette.indigo.dark,
            }}
            topElement={<Logo size={70} type="community" url={community.logoURL}></Logo>}
            onClose={newInCommunity.closeMessage}
          >
            <p>
              {joinedAs} recently joined{' '}
              <Link
                className={isDnbCommunity ? 'text-link-light' : 'text-link'}
                to={communityUrls.overview(community.slug)}
              >
                {community.name}
              </Link>
            </p>
          </DashboardMessage>
        );
      }

      case 'pending-community-invites': {
        if (pendingInvites.isClosed) return null;
        return message.values.length > 0 ? (
          <DashboardMessage
            key={message.type}
            topElement={<Logo size={70} type="community" url={message.values[0].community.logoURL}></Logo>}
            onClose={pendingInvites.closeMessage}
          >
            You have been invited to join {message.values[0].community.name}
            <div style={{ marginTop: 'auto' }}>
              <Link
                className="text-link"
                to={communityUrls.join(message.values[0].community.slug, message.values[0].inviteCode)}
              >
                View invite
              </Link>
            </div>
          </DashboardMessage>
        ) : null;
      }
      case 'latest-partner-offer': {
        if (partnerOffer.isClosed || partnerOffer.isLoading) return null;

        const linkInfo = getLinkInfo(message, userProfile);
        const linkText = message.linkText || 'Learn more';

        return (
          <DashboardMessage
            key={message.type}
            topElement={<Logo size={70} type="organization" url={message.logoUrl} />}
            onClose={partnerOffer.closeMessage}
          >
            <h3 className="text-body text-weight-medium">You are eligible for our latest network-partner deal</h3>
            {message.description && (
              <p
                className="text-body"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.5,
                  marginTop: quarterSpacing,
                  whiteSpace: 'pre-line',
                }}
              >
                {stripHtml(message.description)}
              </p>
            )}
            <div style={{ marginTop: 'auto' }}>
              {linkInfo.isInternal ? (
                <Link className="text-link" to={linkInfo.href}>
                  {linkText}
                </Link>
              ) : (
                <a className="text-link" href={linkInfo.href} target="_blank" rel="noopener noreferrer">
                  {linkText}
                </a>
              )}
            </div>
          </DashboardMessage>
        );
      }

      case 'mentor-with-no-mentees':
        if (mentorNoMentees.isClosed) return null;
        return (
          <DashboardMessage
            key={message.type}
            color={{
              background: bluePlanetTheme.bluePlanetPalette.blue.main,
              text: bluePlanetTheme.bluePlanetPalette.white,
            }}
            topElement={<div style={{ height: 70, width: 70 }} />}
            onClose={mentorNoMentees.closeMessage}
          >
            <p className="u-content-spacing-bottom">
              Feel free to explore and follow the companies you like. Make sure that you have given thorough information
              about your experience and skills. This will help the program manager connect you with a suitable company.
            </p>
            <div style={{ marginTop: 'auto' }}>
              <Link className="text-link-light" to={userUrls.profile.edit}>
                Update profile
              </Link>
            </div>
          </DashboardMessage>
        );

      case 'mentor-with-one-mentee':
        if (mentorOneMentee.isClosed) return null;
        return (
          <DashboardMessage
            key={message.type}
            color={{
              background: bluePlanetTheme.bluePlanetPalette.blue.main,
              text: bluePlanetTheme.bluePlanetPalette.white,
            }}
            topElement={<Logo size={70} type="company" url={message.company.logoURL} />}
            onClose={mentorOneMentee.closeMessage}
          >
            <p className="u-content-spacing-bottom">You are currently mentoring {message.company.name}</p>
            <div style={{ marginTop: 'auto' }}>
              <Link className="text-link-light" to={companyUrls.overview(message.company.slug, 'profile')}>
                Show mentorship
              </Link>
            </div>
          </DashboardMessage>
        );

      case 'mentor-with-several-mentees':
        if (mentorSeveralMentees.isClosed) return null;
        return (
          <DashboardMessage
            key={message.type}
            color={{
              background: bluePlanetTheme.bluePlanetPalette.blue.main,
              text: bluePlanetTheme.bluePlanetPalette.white,
            }}
            topElement={<div style={{ height: 70, width: 70 }} />}
            onClose={mentorSeveralMentees.closeMessage}
          >
            <p className="u-content-spacing-bottom">You are currently mentoring {message.count} companies.</p>
            <div style={{ marginTop: 'auto' }}>
              <Link className="text-link-light" to={userUrls.profile.edit}>
                View mentorships
              </Link>
            </div>
          </DashboardMessage>
        );

      default:
        return null;
    }
  };

  const messageCards = messages
    .filter(
      msg => msg.type !== 'welcome-to-company-profile-as-new-company-admin' && msg.type !== 'raising-capital-2025',
    )
    .map((message, index) => renderMessageCard(message, index))
    .filter(notEmpty);

  const companyMessagesArray = CompanyMessages({
    messages,
    excludeMessages: ['welcome-to-company-profile-as-new-company-admin', 'raising-capital-2025'],
  }).filter(notEmpty);

  const hasAnyMessages = messageCards.length > 0 || companyMessagesArray.length > 0;

  if (!hasAnyMessages) {
    return null;
  }

  return (
    <MobileMargin>
      <div className="u-flex u-flex--column" style={{ gap: '1rem' }}>
        {companyMessagesArray}
        {messageCards}
      </div>
    </MobileMargin>
  );
}
